/**
 * Pure filter helpers for liveEvents — no React, no side effects.
 */

import { getLocationPartId, buildLocationMaps } from './locationPartMapping';
import { severityToKey } from './conditionMapping';
import { ACTIVITY_TYPE_TO_REGISTRY_KEYS } from '../models/registry';

/**
 * Returns true when a task's `component_type` (the DB high-level type, e.g.
 * "motor") is compatible with a 3D-registry key (e.g. "ac_induction_motor").
 * Used to restrict machine-level tasks and repairs to the components they
 * belong to.
 *
 * The mapping is derived from the model registry's descriptors rather than
 * kept here. The hand-maintained copy that used to live in this file had
 * drifted: transformer, centrifugal_pump, centrifugal_fan and solid_coupling
 * were missing entirely, so a machine-level task or repair on any of those four
 * silently never surfaced on hover, and `fan` mapped only to the compressor
 * stand-in, skipping centrifugal_fan.
 */
export function taskComponentTypeMatchesRegistryKey(taskComponentType, registryKey) {
  if (!taskComponentType || !registryKey) return false;
  const allowed = ACTIVITY_TYPE_TO_REGISTRY_KEYS[String(taskComponentType).toLowerCase()];
  if (!allowed) return false;
  return allowed.indexOf(String(registryKey).toLowerCase()) !== -1;
}

/**
 * Terminal states from the activities API. An item in any of these is history:
 * it must not tint geometry, raise a marker, or count toward an open-item list.
 */
const CLOSED_STATES = new Set([
  'closed', 'resolved', 'cleared', 'complete', 'completed',
]);

/**
 * Whether an activity is still open. The activities API returns closed items
 * unless a state filter is supplied, and the heatmap has no expiry of its own,
 * so without this check a critical alarm resolved months ago keeps its red
 * tint and floating badge forever.
 */
export function isOpenActivity(ev) {
  if (!ev) return false;
  return !CLOSED_STATES.has(String(ev.state || '').toLowerCase());
}

/**
 * Activity feed items with type "event" are alarm/evaluation records. Template
 * names (event_type) are often "New Event Definition" or "Rms Acceleration" —
 * they do not always contain the word "alarm", so also inspect message text.
 */
export function isAlarmEvent(ev) {
  if (!ev) return false;
  const primary = (ev.type || '').toLowerCase();
  if (primary === 'task' || primary === 'repair' || primary === 'note' || primary === 'diagnosis') {
    return false;
  }
  if (primary === 'event') return true;
  const label = (ev.event_type || '').toLowerCase();
  const text = `${ev.message || ''} ${ev.title || ''}`.toLowerCase();
  return (
    label.includes('alarm')
    || label.includes('critical')
    || label.includes('warning')
    || text.includes('alarm')
  );
}

/**
 * The backend's ActivityTypeEnum (app/models/helpers/enums.py) collapsed to the
 * four kinds the UI actually distinguishes. `attachment` is deliberately absent
 * — it has no feed row of its own — and falls through to 'default'.
 */
const TYPE_TO_KIND = Object.assign(Object.create(null), {
  task: 'task',
  repair: 'repair',
  event: 'alarm',
  note: 'insight',
  diagnosis: 'insight',
  analyst_summary: 'insight',
  ahs_feedback: 'insight',
});

/**
 * Classifies an activity into 'alarm' | 'task' | 'repair' | 'insight' | 'default'.
 *
 * Note 'default' has no filter chip, so items landing there (an `attachment`, or
 * anything with no `type` and an unrecognised `event_type`) are reachable only
 * under All.
 *
 * Classify on `type` first. `event_type` holds the *template* name — real values
 * are "Rms Acceleration", "New Event Definition", "Velocity Overall" — so the
 * substring tests below match almost nothing on their own. Three call sites
 * previously classified on `event_type` alone, which is why the Alarms and
 * Repair chips matched no rows and most feed items rendered with the grey
 * fallback icon.
 *
 * The `event_type` branches are still needed: the v2 snapshot endpoint
 * (digital_twin_controller) emits `event_type` with no `type` at all.
 */
export function getEventKind(ev) {
  if (!ev) return 'default';
  const byType = TYPE_TO_KIND[String(ev.type || '').toLowerCase()];
  if (byType) return byType;
  const label = String(ev.event_type || '').toLowerCase();
  if (TYPE_TO_KIND[label]) return TYPE_TO_KIND[label];
  if (label.includes('task')) return 'task';
  if (label.includes('repair')) return 'repair';
  if (label.includes('insight')) return 'insight';
  if (isAlarmEvent(ev)) return 'alarm';
  return 'default';
}

/**
 * Returns the events whose component_id matches `componentId`, OR whose
 * `location_id` resolves to `componentId` via `sceneLocations`. Live events
 * sourced from the activity API often carry a location_id only (alarms are
 * bearing-scoped server-side), so callers that omit `sceneLocations` will
 * silently drop those — always pass scene.locations when you have it.
 */
export function getEventsForComponent(liveEvents, componentId, sceneLocations) {
  if (!liveEvents || componentId == null) return [];
  const id = String(componentId);
  // Use consolidated buildLocationMaps; extract just the component map
  const locMap = buildLocationMaps(sceneLocations).locationToComponentMap;
  return liveEvents.filter((ev) => {
    if (ev.component_id != null && String(ev.component_id) === id) return true;
    if (ev.location_id != null && locMap[String(ev.location_id)] === id) return true;
    return false;
  });
}

export function getActiveAlarmsForComponent(liveEvents, componentId, sceneLocations) {
  return getEventsForComponent(liveEvents, componentId, sceneLocations).filter(isAlarmEvent);
}

/**
 * `modelKey` is the owning body's resolved registry key. It only matters on the fallback
 * branch: enrichActivityEvents normally bakes `part_id` in at fetch time, but it captures
 * sceneLocations BEFORE the request, so events that arrive before the scene has loaded carry
 * no part_id and are resolved here instead. Without the key those events take the end-word
 * reading while the heatmap takes the per-shaft one, and a tinted gearbox half then reports
 * no alarms when you hover it.
 */
export function resolveEventPartId(ev, sceneLocations, modelKey) {
  if (!ev) return null;
  if (ev.part_id) return ev.part_id;
  if (ev.location_id == null) return null;
  const loc = (sceneLocations || []).find(
    l => l && String(l.id) === String(ev.location_id)
  );
  return loc ? getLocationPartId(loc, modelKey) : null;
}

export function getActiveAlarmsForPart(liveEvents, componentId, partId, sceneLocations, modelKey) {
  const all = getActiveAlarmsForComponent(liveEvents, componentId, sceneLocations);
  if (!partId) return all;
  if (partId === 'body') {
    // 'body' means "no specific section resolved" — alarms scoped to a real
    // part (drive_end, bushings, ...) must not bleed into the body hover, or
    // they would double-count under both the part and the body.
    return all.filter(ev => resolveEventPartId(ev, sceneLocations, modelKey) === null);
  }
  return all.filter(
    ev => resolveEventPartId(ev, sceneLocations, modelKey) === String(partId)
  );
}

/**
 * Machine-scoped alarm events (no component_id/location_id) that apply to the
 * hovered component type — mirrors machine-level task/repair surfacing.
 */
export function getMachineLevelAlarmsForComponent(liveEvents, registryKey) {
  if (!liveEvents) return [];
  return liveEvents.filter((ev) => {
    if (!isAlarmEvent(ev)) return false;
    if (ev.component_id != null || ev.location_id != null) return false;
    if (!ev.component_type) return true;
    if (!registryKey) return true;
    return taskComponentTypeMatchesRegistryKey(ev.component_type, registryKey);
  });
}

/**
 * Returns open tasks or repairs that are not tied to any specific component or
 * location (i.e. machine-level activities). These should be surfaced on hover
 * regardless of which component is hovered.
 *
 * @param {Array}  liveEvents - Full liveEvents array from Redux.
 * @param {string} itemType   - 'task' or 'repair'.
 */
export function getMachineLevelOpenItems(liveEvents, itemType) {
  if (!liveEvents) return [];
  return liveEvents.filter((ev) => {
    const t = (ev.type || ev.event_type || '').toLowerCase();
    return (
      t === itemType
      && isOpenActivity(ev)
      && ev.component_id == null
      && ev.location_id == null
    );
  });
}

function severityRankFromKey(key) {
  if (key === 'high') return 3;
  if (key === 'medium') return 2;
  if (key === 'low') return 1;
  return 0;
}

/** Returns 'high' | 'medium' | 'low' | 'default' | null for a set of events. */
export function worstSeverity(events) {
  if (!events || events.length === 0) return null;
  let worst = 0;
  events.forEach((ev) => {
    const r = severityRankFromKey(severityToKey(ev.severity));
    if (r > worst) worst = r;
  });
  if (worst === 3) return 'high';
  if (worst === 2) return 'medium';
  if (worst === 1) return 'low';
  return 'default';
}
